import { Component, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { GridOptions } from 'ag-grid';
import { AutoroutingService } from '../../autorouting.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProvidersService } from '../../../providers/providers.service';
import { IMyDrpOptions } from 'mydaterangepicker';
import { EventEmitter } from '@angular/common/src/facade/async';
import { T2CardComponent } from '../../../../trip2-theme/components/t2-card/t2-card.component';
import { T2LoadingService } from '../../../../trip2-theme/components/t2-loading/t2-loading.service';
import { Subscription } from 'rxjs/Subscription';
import { ActivatedRoute } from '@angular/router';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { SignalRFacadeService } from 'app/services/signal-r-facade.service';
import { constant } from 'app/app.constants';
import { IHubConnection, ISignalRError } from 'app/core/interfaces/signal.interface';
import { isHubConnection, isSignalRError } from 'app/core/signalr-type-guards.ts/signalr-type-guards';

@Component({
  selector: 'app-autorouting-jobs',
  templateUrl: './autorouting-jobs.component.html',
  styleUrls: ['./autorouting-jobs.component.scss']
})
export class AutoroutingJobsComponent implements OnInit, OnChanges, OnDestroy {
  private gridTemplate: GridOptions;
  private autoroutingJobsForm: FormGroup;
  private selectAllStatus: any;
  private selectProvider: any;
  private jobStatusId: any;
  private providerId: any;
  public jobsSelected = 0;
  public openExport: any;
  private observables: Subscription[] = [];
  private connection?: string;
  private dispatchHubConnection = 'AutoroutingChanged';

  @Input() beginEndParameters = null;
  @Input() providerParameters = null;
  @Input() updateGridJobs = [];
  @Input() reloadGird: any;

  @Output() countJobs: EventEmitter<any> = new EventEmitter<any>();
  @Output() reloadSchedule: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('jobsResults') modal: T2CardComponent;
  myDateRangePickerOptions: IMyDrpOptions = {
    dateFormat: 'yyyy-mm-dd',
  };

  constructor(
    private _autoroutingJobsService: AutoroutingService,
    protected _providerService: ProvidersService,
    protected fb: FormBuilder,
    protected _loading: T2LoadingService,
    private signalR: SignalRFacadeService,
    private _activatedRoute: ActivatedRoute,
    private sweetAlertHelper: SweetAlertHelper,
  ) {
    this.gridTemplate = <GridOptions>{
      getRowStyle: this.getRowStyle,
    };
    this.gridTemplate.columnDefs = this.columnDefs();
    this.gridTemplate.getRowNodeId = (data) => {
      return data.autoroutingjobid;
    };
    this.createForm();

    // TODO AutoAjuste del Ancho de las Columnas - Cargar Grid Vacia
    this.gridTemplate.onGridReady = () => {
      if (this.gridTemplate && this.gridTemplate.api) {
        this.gridTemplate.api.sizeColumnsToFit();
        this.gridTemplate.api.setRowData([]);
      }
    };
  }

  ngOnInit() {
    this.getSelectAllStatus();
    this.getSelectProvider();
    this.initSignalR();
  }

  initSignalR() {
    this.observables.push(
      this.signalR.watchConnection$().subscribe({
        next: (res: IHubConnection | ISignalRError | null) => {
           if (!res) {
            return;
          }
          if (isSignalRError(res)) {
            this.sweetAlertHelper.captureException(res.message);
            return;
          }
          if (isHubConnection(res)) {
            this.connection = res.id;
            this.setupHubSubscription();
          }
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        }
      })
    );
  }

  private setupHubSubscription() {
    this.observables.push(
      this.signalR
        .registerHubConnection(constant.URL_HUB_SERVICE_DISPATCH)
        .subscribe({
          next: () => {
            this.signalR.subscribeTo(
              this.dispatchHubConnection,
              (id: string, status: any, statusid: number) => {
                const node = this.gridTemplate.api.getRowNode(id);
                if (!node || !node.data) {
                  return;
                }
                if (!node.data.autoroutingstatus) {
                  node.data.autoroutingstatus = {};
                }
                node.data.autoroutingstatus.description = status;
                node.data.autoroutingstatus.autoroutingstatusid = statusid;
                if (this.gridTemplate && this.gridTemplate.api) {
                  this.gridTemplate.api.refreshView();
                }
                this.refreshResults();
                if (statusid === 4) {
                  this.reloadSchedule.emit(true);
                }
              }
            );
            this.registerHub();
          },
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
          }
        })
    );
  }

  private refreshResults(): void {
    if (!this.beginEndParameters || !this.providerParameters) {
      return;
    }

    this.getResult(
      this.beginEndParameters,
      this.beginEndParameters,
      this.providerParameters,
      0,
    );
  }

  private registerHub() {
    if (this.connection && this.providerParameters) {
      const date = this.formatDate(this.beginEndParameters);
      this.observables.push(
        this._autoroutingJobsService.registerJobsSignalR(this.connection, date, this.providerParameters).subscribe({
          next: () => {},
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
          }
        }
      ));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['beginEndParameters'] || changes['providerParameters']) {
      if (this.beginEndParameters && this.providerParameters) {
        this.getResult(this.beginEndParameters, this.beginEndParameters, this.providerParameters, 0);
        this.registerHub();
      }
    }

    if (changes['updateGridJobs']) {
      if (this.updateGridJobs && this.updateGridJobs.length !== 0) {
        if (this.beginEndParameters && this.providerParameters) {
          this.getResult(this.beginEndParameters, this.beginEndParameters, this.providerParameters, 0);
          this.registerHub();
        }
      }
    }

    if (changes['reloadGird']) {
      if (this.beginEndParameters && this.providerParameters) {
        this.getResult(this.beginEndParameters, this.beginEndParameters, this.providerParameters, 0);
        this.registerHub();
      }
    }
  }

  /**
   * @method exportJob
   * @description to download the Autorouting in excel
   */
  exportJob(event) {
    if (event.value === 'Finished') {
      this.observables.push(
        this._autoroutingJobsService
          .getAutoroutingExport(event.data.autoroutingjobid)
          .subscribe(
            success => {
              const blob = success.blob();
              const downloadUrl = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = downloadUrl;
              a.download = 'Autorouting'+event.data.autoroutingjobid+'.xlsx';
              document.body.appendChild(a);
              a.click();
            }
          ));
    }
  }

  /**
   * @method cellClicJob
   * @description Manage the methods of a click on the grid
   */
  cellClicJob(event){
    if(event.colDef.colId === 'export') {
      this.exportJob(event);
    }
    if (event.colDef.colId === 'autoroutingstatus.autoroutingstatusid') {
      this.deleteJob(event);
    }
  }

  columnDefs() {
    return [
      {
        headerName: 'Job',
        field: 'autoroutingjobid',
        colId: 'autoroutingjobid',
        width: 100
      },
      {
        headerName: 'Created',
        field: 'addedon',
        colId: 'addedon',
        width: 150
      },
      {
        headerName: 'Status',
        field: 'autoroutingstatus.description',
        colId: 'description',
        width: 80
      },
      {
        headerName: 'User',
        field: 'adminuser.fullname',
        colId: 'fullname',
        width: 100
      },
      {
        headerName: 'Action',
        field: 'autoroutingstatus.autoroutingstatusid',
        colId: 'autoroutingstatusid',
        // cellClass: ['icofont-trash'],
        cellStyle: {
          'font-size': '15px'
        },
        width: 65,
        cellRenderer: (params) => {
          let ico = '';
          if (
            params.data.autoroutingstatus.autoroutingstatusid === 1) {
            ico = 'icofont-trash color-danger';
          }
          if (params.data.autoroutingstatus.autoroutingstatusid === 2) {
            ico = 'fa fa-spinner color-warning';
          }
          if (params.data.autoroutingstatus.autoroutingstatusid === 3) {
            ico = 'fas fa-clipboard-check color-secondary';
          }
          if (params.data.autoroutingstatus.autoroutingstatusid === 4) {
            ico = 'fa fa-check color-success';
          }
          if (params.data.autoroutingstatus.autoroutingstatusid === 5 || params.data.autoroutingstatus.autoroutingstatusid === 6) {
            ico = 'fas fa-exclamation-circle color-danger';
          }
          return `<i class="${ico}" aria-hidden="true"></i>`;
        },
      },
      {
        headerName: 'Export',
        field: 'autoroutingstatus.description',
        colId: 'export',
        width: 65,
        cellRenderer: (params) => {
          let ico = '';
          if (
            params.data.autoroutingstatus.autoroutingstatusid === 3) {
            ico = 'far fa-file-excel color-success';
          }
          if (params.data.autoroutingstatus.autoroutingstatusid !== 3) {
            ico = 'fas fa-ban color-danger';
          }
          return `<i class="${ico}" aria-hidden="true"></i>`;
        },
      },
      {
        headerName: 'Total Trips',
        field: 'totaltrips',
        colId: 'totaltrips',
        width: 100,
      },
      {
        headerName: 'Total Vehicles',
        field: 'totalvehicles',
        colId: 'totalvehicles',
        width: 100,
      },
      {
        headerName: 'Trips Assigned',
        field: 'tripsassigned',
        colId: 'tripsassigned',
        width: 100,
      },
      {
        headerName: 'Vehicles Assigned',
        field: 'vehiclesassigned',
        colId: 'vehiclesassigned',
        width: 100,
      }
    ];
  }


  createForm () {
    this.autoroutingJobsForm = this.fb.group({
      jobStatusId: ['', Validators.required],
      providerId: ['', Validators.required],
      myDateRange: ['', Validators.required],
    });
  }

  getSelectAllStatus() {
    this.observables.push(
      this._autoroutingJobsService.getAutoroutingStatus()
        .subscribe(data => {
          this.selectAllStatus = data;
        }));
  }

  getSelectProvider() {
    this.observables.push(
      this._providerService.getCompanies('All').subscribe(data => {
        this.selectProvider = data;
      }));
  }

  saveAction(data) {
    this.getResult(data.myDateRange.beginDate, data.myDateRange.endDate, data.providerId, data.jobStatusId);
  }

  formatDate(data) {
    if (typeof data === 'string') {
      return data;
    } else {
      return data.year + '-' + (data.month < 10 ? '0' : '') + data.month + '-' + (data.day < 10 ? '0' : '') + data.day;
    }
  }

  getResult(begin, end, provider, status) {
    this.observables.push(
      this._autoroutingJobsService
        .getAutoroutingJobs(
          this.formatDate(begin),
          this.formatDate(end),
          provider,
          status)
        .subscribe( resp => {
          this.gridTemplate.api.setRowData(resp);
          this.gridTemplate.api.sizeColumnsToFit();
          this.countJobs.emit(this.gridTemplate.api.getDisplayedRowCount());
        }));
  }

  openResults($event) {
    const statusId = $event.data.autoroutingstatus.autoroutingstatusid;
    if (statusId === 3 || statusId === 4 || statusId === 5) {
      this.jobsSelected = $event.data.autoroutingjobid;
      if (statusId === 3) {
        this.openExport = true;
      }

      // this.modal.open();
      const params = [
        'height=' + screen.height,
        'width=' + screen.width,
        'fullscreen=yes',
        'location=no'
      ].join(',');
      window.open('#/autorouting/' + $event.data.autoroutingjobid, '', params);
    }
  }

  getRowStyle(params) {
    if (params.data.autoroutingstatus.autoroutingstatusid === 6 || params.data.autoroutingstatus.autoroutingstatusid === 5) {
      return {
        'background-color': '#ffa2a3',
        'color': '#000000'
      };
    }
  }

  get actualPath(): string {
    return this._activatedRoute.snapshot.url[0].path;
  }

  deleteJob($event) {
    if ($event.value === 1) {
      this._loading.show();
      this.sweetAlertHelper.createCustomAlert({
        title: 'Are you sure to want delete this job',
        text: 'this is process is irreversible!',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.value) {
          this.observables.push(
            this._autoroutingJobsService.deleteAutoroutingJob($event.data.autoroutingjobid)
              .finally(() => {
                this._loading.hiden();
              })
              .subscribe(res => {
                this.sweetAlertHelper.createCustomAlert({
                  title: 'Deleted!',
                  text: 'Your Job has been deleted.',
                  type: 'success',
                });
                this.gridTemplate.api.removeItems([$event.node]);
                this.countJobs.emit(
                  this.gridTemplate.api.getDisplayedRowCount()
                );
              }));
        } else {
          this._loading.hiden();
        }
      });
    }
  }

  private removeFromGroup(): void {
    if (this.connection && this.beginEndParameters && this.providerParameters) {
      const date = this.formatDate(this.beginEndParameters);
      this._autoroutingJobsService
        .deleteJobsSignalR(this.connection, date, this.providerParameters)
        .subscribe({
          next: () => {},
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
          }
        });

      this.signalR.deleteHubConnection(constant.URL_HUB_SERVICE_DISPATCH).subscribe({
        next: () => {},
        error: (error) => {
          this.sweetAlertHelper.captureException(error);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.removeFromGroup();
    this.observables.forEach((item) => item.unsubscribe());
    this.signalR.unsubscribeTo(this.dispatchHubConnection);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/autorouting/components/autorouting-jobs/autorouting-jobs.component.ts